import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import { ComponentActionsContainer } from '@/catalog-apps/components/component-library/shared/ComponentActionsContainer'
import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import {
  normalizeRechartsData,
  remapRechartsRecordKeys,
} from '@/catalog-apps/components/component-library/shared/rechartsDataUtils'
import { ExportButtonV2 } from '@/catalog-apps/components/component-library/DataTableV2/components/ExportButtonV2'
import { DataTableV2DataRow } from '@/catalog-apps/components/component-library/DataTableV2/types'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/shadcn/charts'
import { cn } from '@/lib/utils'

import { AiBarChartSchema } from './types-ai-bar-chart'

/** Default tilt for category labels in the chat panel so long names fit without overlapping. */
const DEFAULT_X_AXIS_LABEL_ANGLE = -35

/** Matches `--chart-1` ... `--chart-5` on `:root` and `.dark` in `index.scss`. */
const THEME_CHART_COLOR_SLOT_COUNT = 5

const DEFAULT_CATEGORY_SIZE = 56
const DEFAULT_HORIZONTAL_CATEGORY_SIZE = 72
const DEFAULT_MAX_VISIBLE_CATEGORY_COUNT = 8
const DEFAULT_MIN_CHART_HEIGHT = 280
const DEFAULT_MIN_CHART_WIDTH = 520

function themeChartFillHsl(seriesIndex: number): string {
  const slot = (seriesIndex % THEME_CHART_COLOR_SLOT_COUNT) + 1
  return `hsl(var(--chart-${slot}))`
}

export function AiBarChart({ schema }: { schema: AiBarChartSchema }) {
  const layout = schema.config.layout ?? 'horizontal'
  const isVerticalLayout = layout === 'vertical'
  const xAxisLabelAngle = schema.config.xAxisLabelAngle ?? DEFAULT_X_AXIS_LABEL_ANGLE
  const xAxisTickHeight =
    schema.config.xAxisTickHeight ??
    (xAxisLabelAngle !== 0 && !isVerticalLayout ? 56 : schema.config.hideXAxis ? 0 : 28)
  const maxVisibleCategoryCount = schema.config.maxVisibleCategoryCount ?? DEFAULT_MAX_VISIBLE_CATEGORY_COUNT
  const categorySize =
    schema.config.categorySize ?? (isVerticalLayout ? DEFAULT_CATEGORY_SIZE : DEFAULT_HORIZONTAL_CATEGORY_SIZE)
  const isScrollable = schema.data.length > maxVisibleCategoryCount
  const { data: chartData, mapDataKey } = normalizeRechartsData(schema.data, [
    schema.config.categoryKey,
    ...schema.config.barDataKeys,
  ])
  const normalizedCategoryKey = mapDataKey(schema.config.categoryKey)
  const normalizedSeriesConfig = remapRechartsRecordKeys(schema.config.seriesConfig, mapDataKey)
  const normalizedBarDataKeys = schema.config.barDataKeys.map(mapDataKey)
  const chartHeight = isVerticalLayout
    ? Math.max(schema.config.minChartHeight ?? DEFAULT_MIN_CHART_HEIGHT, schema.data.length * categorySize)
    : (schema.config.minChartHeight ?? DEFAULT_MIN_CHART_HEIGHT)
  const chartWidth = !isVerticalLayout
    ? Math.max(schema.config.minChartWidth ?? DEFAULT_MIN_CHART_WIDTH, schema.data.length * categorySize)
    : (schema.config.minChartWidth ?? DEFAULT_MIN_CHART_WIDTH)

  const exportData = useMemo<DataTableV2DataRow[]>(
    () =>
      schema.data.map((item, index) => {
        const exportRow: DataTableV2DataRow = {
          id: index + 1,
          [schema.config.categoryKey]: item[schema.config.categoryKey],
        }

        schema.config.barDataKeys.forEach((key) => {
          const seriesLabel = schema.config.seriesConfig[key]?.label ?? key
          exportRow[seriesLabel] = item[key]
        })

        return exportRow
      }),
    [schema]
  )

  return (
    <div className="flex h-[50vh] w-full flex-col overflow-hidden">
      <ComponentHeader emphasize wrapperClassName="rounded-b-none">
        <ComponentTitle title={schema.title} description={schema.description} />
        <ComponentActionsContainer className="bg-transparent p-0">
          <ExportButtonV2
            data={exportData}
            title={schema.title}
            selectionRequired={false}
            className="text-background dark:text-foreground"
          />
        </ComponentActionsContainer>
      </ComponentHeader>
      <div
        className={cn(
          'min-h-0 flex-1 rounded-md border bg-background',
          isScrollable ? 'overflow-auto' : 'overflow-hidden'
        )}
      >
        <ChartContainer
          className={cn('mx-auto aspect-auto min-h-0', isScrollable ? 'justify-start' : 'h-full w-full')}
          config={normalizedSeriesConfig}
          style={
            isScrollable
              ? {
                  height: isVerticalLayout ? chartHeight : '100%',
                  minHeight: chartHeight,
                  minWidth: chartWidth,
                  width: isVerticalLayout ? '100%' : chartWidth,
                }
              : undefined
          }
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            layout={layout}
            barCategoryGap={schema.config.barCategoryGap ?? '18%'}
            barGap={schema.config.barGap ?? 4}
            maxBarSize={schema.config.maxBarSize}
            margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
          >
            {schema.config.showGrid && (
              <CartesianGrid strokeDasharray="3 3" horizontal={!isVerticalLayout} vertical={isVerticalLayout} />
            )}
            <XAxis
              dataKey={isVerticalLayout ? undefined : normalizedCategoryKey}
              hide={schema.config.hideXAxis}
              orientation={schema.config.xAxisOrientation}
              type={isVerticalLayout ? 'number' : 'category'}
              angle={isVerticalLayout ? 0 : xAxisLabelAngle}
              textAnchor={xAxisLabelAngle === 0 || isVerticalLayout ? 'middle' : 'end'}
              height={schema.config.hideXAxis ? undefined : xAxisTickHeight}
              interval={isScrollable ? 0 : undefined}
            />
            <YAxis
              dataKey={isVerticalLayout ? normalizedCategoryKey : undefined}
              hide={schema.config.hideYAxis}
              orientation={schema.config.yAxisOrientation}
              type={isVerticalLayout ? 'category' : 'number'}
              width={isVerticalLayout ? (schema.config.yAxisTickWidth ?? 112) : undefined}
              interval={isScrollable ? 0 : undefined}
            />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
            {schema.config.showLegend && <ChartLegend content={<ChartLegendContent />} />}
            {schema.config.barDataKeys.map((key, seriesIndex) => {
              const barConfig = schema.config.seriesConfig[key as keyof typeof schema.config.seriesConfig]
              const fill = barConfig?.color ?? themeChartFillHsl(seriesIndex)
              return (
                <Bar
                  key={key}
                  dataKey={normalizedBarDataKeys[seriesIndex]}
                  fill={fill}
                  radius={barConfig?.radius ?? 3}
                  barSize={barConfig?.barSize ?? schema.config.barSize}
                  stackId={
                    schema.config.stacked
                      ? (barConfig?.stackId ?? schema.config.stackId ?? 'stack')
                      : barConfig?.stackId
                  }
                />
              )
            })}
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
